import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  RotateCcw,
  ArrowLeftRight,
  Calendar,
  Search,
  Download,
  RefreshCw,
  Eye,
  Receipt,
  User,
  Package,
  DollarSign,
  Filter,
  TrendingDown,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NASRefundHistory = () => {
  const [refunds, setRefunds] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingRefund, setViewingRefund] = useState(null);
  
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [cashiers, setCashiers] = useState([]);

  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const loadRefunds = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/api/nas/pos/refunds?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}&`;
      if (typeFilter !== 'all') url += `refund_type=${typeFilter}&`;
      if (cashierFilter !== 'all') url += `processed_by=${encodeURIComponent(cashierFilter)}&`;
      
      const res = await axios.get(url);
      setRefunds(res.data || []);
    } catch (error) {
      console.error('Error loading refunds:', error);
      toast.error('Failed to load refund history');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      let url = `${API}/api/nas/pos/refunds/stats?`;
      if (dateFrom) url += `date_from=${dateFrom}&`;
      if (dateTo) url += `date_to=${dateTo}`;
      
      const res = await axios.get(url);
      setStats(res.data);
      setCashiers(res.data.cashiers || []);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadRefunds();
    loadStats();
    // eslint-disable-next-line
  }, []);

  const handleApplyFilters = () => {
    loadRefunds();
    loadStats();
  };

  const filteredRefunds = refunds.filter(refund => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      refund.refundNumber?.toLowerCase().includes(search) ||
      refund.originalReceiptNumber?.toLowerCase().includes(search) ||
      refund.processedBy?.toLowerCase().includes(search)
    );
  });

  const exportToCSV = () => {
    const headers = ['Refund #', 'Date', 'Original Receipt', 'Type', 'Items', 'Amount', 'Processed By', 'Reason'];
    const rows = filteredRefunds.map(r => [
      r.refundNumber,
      new Date(r.createdAt).toLocaleString('en-PH'),
      r.originalReceiptNumber,
      r.refundType,
      r.items?.length || 0,
      r.refundAmount,
      r.processedBy,
      r.reason || ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `refund-history-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    toast.success('Exported to CSV');
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <RotateCcw className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-amber-700">Total Returns</p>
                  <p className="text-2xl font-bold text-amber-900">{stats.totalCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-red-700">Refund Amount</p>
                  <p className="text-xl font-bold text-red-900">{formatCurrency(stats.totalRefundAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <ArrowLeftRight className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-700">Exchange Value</p>
                  <p className="text-xl font-bold text-blue-900">{formatCurrency(stats.totalExchangeAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500 rounded-lg">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-700">Items Returned</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalItemsReturned}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter size={18} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">From Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">To Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="refund">Refunds Only</SelectItem>
                  <SelectItem value="exchange">Exchanges Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Processed By</Label>
              <Select value={cashierFilter} onValueChange={setCashierFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cashiers</SelectItem>
                  {cashiers.map((cashier, idx) => (
                    <SelectItem key={idx} value={cashier}>{cashier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">&nbsp;</Label>
              <Button onClick={handleApplyFilters} className="w-full bg-emerald-600 hover:bg-emerald-700">
                <RefreshCw size={16} className="mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>
          
          {/* Search and Export */}
          <div className="flex gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by refund #, receipt #, or cashier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={exportToCSV}>
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Refund List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RotateCcw size={18} />
              Refund History
            </span>
            <span className="text-sm font-normal text-slate-500">
              {filteredRefunds.length} record(s)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredRefunds.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <RotateCcw size={48} className="mx-auto mb-4 opacity-50" />
              <p>No refunds found for the selected filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRefunds.map((refund, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                    refund.refundType === 'exchange' 
                      ? 'bg-blue-50 border-blue-200 hover:border-blue-300' 
                      : 'bg-amber-50 border-amber-200 hover:border-amber-300'
                  }`}
                  onClick={() => setViewingRefund(refund)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        refund.refundType === 'exchange' ? 'bg-blue-500' : 'bg-amber-500'
                      }`}>
                        {refund.refundType === 'exchange' 
                          ? <ArrowLeftRight className="h-5 w-5 text-white" />
                          : <RotateCcw className="h-5 w-5 text-white" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{refund.refundNumber}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            refund.refundType === 'exchange' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {refund.refundType === 'exchange' ? 'Exchange' : 'Refund'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Original: {refund.originalReceiptNumber}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(refund.createdAt).toLocaleString('en-PH')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Items</p>
                        <p className="font-semibold">{refund.items?.length || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Processed By</p>
                        <p className="font-medium text-sm">{refund.processedBy}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Amount</p>
                        <p className={`font-bold text-lg ${
                          refund.refundType === 'exchange' ? 'text-blue-700' : 'text-amber-700'
                        }`}>
                          {formatCurrency(refund.refundAmount)}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon">
                        <Eye size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refund Detail Dialog */}
      <Dialog open={!!viewingRefund} onOpenChange={(open) => !open && setViewingRefund(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewingRefund?.refundType === 'exchange' 
                ? <ArrowLeftRight className="text-blue-600" />
                : <RotateCcw className="text-amber-600" />
              }
              {viewingRefund?.refundType === 'exchange' ? 'Exchange' : 'Refund'} Details
            </DialogTitle>
          </DialogHeader>
          
          {viewingRefund && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Refund Number</p>
                  <p className="font-semibold">{viewingRefund.refundNumber}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date & Time</p>
                  <p className="font-medium">{new Date(viewingRefund.createdAt).toLocaleString('en-PH')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Original Receipt</p>
                  <p className="font-medium">{viewingRefund.originalReceiptNumber}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Processed By</p>
                  <p className="font-medium">{viewingRefund.processedBy}</p>
                </div>
              </div>
              
              {/* Items */}
              <div>
                <p className="font-semibold mb-2">Returned Items</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {viewingRefund.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <p className="font-medium">{item.productName}</p>
                        <p className="text-sm text-slate-500">
                          {item.quantity} {item.unit} × {formatCurrency(item.unitPrice)}
                        </p>
                      </div>
                      <p className="font-semibold">{formatCurrency(item.totalPrice)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Reason */}
              {viewingRefund.reason && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-700 font-medium">Reason</p>
                  <p className="text-sm">{viewingRefund.reason.replace(/_/g, ' ')}</p>
                </div>
              )}
              
              {/* Total */}
              <div className={`p-4 rounded-lg ${
                viewingRefund.refundType === 'exchange' 
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">
                    {viewingRefund.refundType === 'exchange' ? 'Exchange Credit' : 'Refund Amount'}
                  </span>
                  <span className={`text-2xl font-bold ${
                    viewingRefund.refundType === 'exchange' ? 'text-blue-700' : 'text-amber-700'
                  }`}>
                    {formatCurrency(viewingRefund.refundAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingRefund(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASRefundHistory;
